import React from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { PAGE_SIZE_OPTIONS } from '../../hooks/usePagination';

interface PaginationProps {
  page: number;
  totalPages: number;
  totalItems: number;
  startIndex: number;
  endIndex: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange: (size: number) => void;
  /** 「n件中 x〜y件」の「件」の前に入れる語。例: '候補者' → 「20候補者中」ではなく件数表示に使う */
  itemLabel?: string;
}

/**
 * 表示するページ番号を組み立てる。
 * 先頭・末尾・現在ページの前後1つを出し、飛ぶところは '…' を入れる。
 * 例: 1 … 4 [5] 6 … 20
 */
export function buildPageNumbers(page: number, totalPages: number): (number | 'ellipsis')[] {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages = new Set<number>([1, totalPages, page, page - 1, page + 1]);
  const visible = Array.from(pages)
    .filter((p) => p >= 1 && p <= totalPages)
    .sort((a, b) => a - b);

  const result: (number | 'ellipsis')[] = [];
  visible.forEach((p, i) => {
    if (i > 0 && p - visible[i - 1] > 1) {
      result.push('ellipsis');
    }
    result.push(p);
  });
  return result;
}

export const Pagination: React.FC<PaginationProps> = ({
  page,
  totalPages,
  totalItems,
  startIndex,
  endIndex,
  pageSize,
  onPageChange,
  onPageSizeChange,
  itemLabel = '件',
}) => {
  // 1ページに収まりきっていて表示件数も既定のままなら、操作するものが無いので出さない
  if (totalItems === 0) {
    return null;
  }

  const pageNumbers = buildPageNumbers(page, totalPages);

  return (
    <div className="flex flex-col sm:flex-row items-center justify-between gap-3 px-6 py-4 border-t border-gray-200 bg-white">
      <div className="flex items-center gap-3 text-sm text-gray-600">
        <span>{`全${totalItems}${itemLabel}中 ${startIndex}〜${endIndex}${itemLabel}を表示`}</span>
        <label className="flex items-center gap-1.5 whitespace-nowrap">
          <span className="text-gray-500">表示件数</span>
          <select
            aria-label="表示件数"
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="border border-gray-300 rounded-md py-1 pl-2 pr-6 text-sm focus:ring-2 focus:ring-green-500 focus:border-transparent"
          >
            {PAGE_SIZE_OPTIONS.map((size) => (
              <option key={size} value={size}>
                {size}
              </option>
            ))}
          </select>
        </label>
      </div>

      {totalPages > 1 && (
        <nav className="flex items-center gap-1" aria-label="ページ送り">
          <button
            type="button"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            aria-label="前のページ"
            className="p-2 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronLeft size={16} />
          </button>

          {pageNumbers.map((p, i) =>
            p === 'ellipsis' ? (
              <span key={`ellipsis-${i}`} className="px-2 text-gray-400 select-none">
                …
              </span>
            ) : (
              <button
                key={p}
                type="button"
                onClick={() => onPageChange(p)}
                aria-label={`${p}ページ目`}
                aria-current={p === page ? 'page' : undefined}
                className={`min-w-[2.25rem] px-2 py-1.5 rounded-md border text-sm transition-colors ${
                  p === page
                    ? 'border-green-600 bg-green-600 text-white font-medium'
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {p}
              </button>
            )
          )}

          <button
            type="button"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            aria-label="次のページ"
            className="p-2 rounded-md border border-gray-300 text-gray-600 hover:bg-gray-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
          >
            <ChevronRight size={16} />
          </button>
        </nav>
      )}
    </div>
  );
};
